import { useState, useRef, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { signOut } from "../../../services/authManager";
import {
  FaClipboardList,
  FaPlus,
  FaSignOutAlt,
  FaStore,
  FaChartBar,
  FaUser,
  FaBuilding,
  FaWhatsapp,
} from "react-icons/fa";
import PermissionGate from "../../common/PermissionGate";
import SupportModal from "../../common/SupportModal";
import { useUser } from "../../../context/UserContext";
import { usePermissions } from "../../../hooks/usePermissions";
import BottomNav from "../BottomNav";
import SellerBottomNav from "../SellerBottomNav";
import logo from "../../../assets/logo.svg";
import UserAvatar from "../../common/UserAvatar";

const Header = () => {
  const auth = useAuth();
  const { user } = useUser();
  const { isSeller } = usePermissions();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const userMenuRef = useRef(null);

  const profilePath = isSeller ? "/seller/profile" : "/profile";

  // Fechar dropdown quando clicar fora dele
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  // Fechar dropdowns quando mudar de rota
  useEffect(() => {
    setUserMenuOpen(false);
  }, [location]);

  const handleSignOut = () => {
    signOut();
    setUserMenuOpen(false);
  };

  // Obter informações do usuário
  const userEmail = auth.user?.profile?.email || "Usuário";
  const userName = auth.user?.profile?.name || userEmail.split("@")[0];

  // Links de navegação
  const buyerNavLinks = [
    {
      name: "Dashboard",
      path: "/",
      exact: true,
      icon: <FaChartBar className="w-5 h-5" />,
    },
    {
      name: "Minhas Cotações",
      path: "/quotes",
      exact: true,
      icon: <FaClipboardList className="w-5 h-5" />,
    },
    {
      name: "Nova Cotação",
      path: "/quotes/new",
      icon: <FaPlus className="w-5 h-5" />,
    },
    {
      name: "Obras",
      path: "/projects",
      icon: <FaBuilding className="w-5 h-5" />,
    },
  ];

  const sellerNavLinks = [
    {
      name: "Dashboard",
      path: "/seller/dashboard",
      exact: true,
      icon: <FaChartBar className="w-5 h-5" />,
    },
    {
      name: "Cotações Recebidas",
      path: "/seller/quotations",
      icon: <FaClipboardList className="w-5 h-5" />,
    },
  ];

  const navLinks = isSeller ? sellerNavLinks : buyerNavLinks;

  const adminLinks = [
    {
      name: "Lojas",
      path: "/stores",
      icon: <FaStore className="w-5 h-5" />,
    },
    {
      name: "Acompanhamento de Cotações",
      path: "/admin/quotations",
      icon: <FaClipboardList className="w-5 h-5" />,
    },
  ];

  // Verificar se o link está ativo
  const isActive = (path, exact) => {
    if (path === "/dashboard") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    if (exact) {
      return location.pathname === path;
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return (
    <div className="flex flex-col lg:flex-row bg-slate-50 min-h-screen">
      {/* Sidebar (desktop only) */}
      <div className="hidden lg:block w-64 overflow-y-auto bg-white shadow-xl flex-shrink-0">
        {/* Logo (mesma altura do header do topo, incluindo a área segura do app) */}
        <div className="flex items-center h-16 box-content px-6 pt-[env(safe-area-inset-top)] bg-white border-b border-slate-200">
          <Link
            to="/"
            className="flex items-center group transition-all duration-200"
          >
            <img 
              src={logo} 
              alt="BuildCheap Logo" 
              className="w-12 h-12 object-contain mr-3"
            />
            <span className="text-xl font-bold text-slate-900 group-hover:text-slate-700 transition-colors duration-200">
              Build<span className="text-accent">Cheap</span>
            </span>
          </Link>
        </div>

        {/* Navigation */}
        {auth.isAuthenticated && (
          <nav className="px-4 py-6 space-y-1">
            {/* Admin Links */}
            {!isSeller && adminLinks.map((link) => (
              <PermissionGate requireAdmin={true} key={link.path}>
                <Link
                  to={link.path}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive(link.path, link.exact)
                      ? "text-slate-900 bg-slate-100 font-medium"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`}
                >
                  <span className="mr-3">{link.icon}</span>
                  {link.name}
                </Link>
              </PermissionGate>
            ))}

            {/* Divider se houver admin links */}
            {!isSeller && (
              <PermissionGate requireAdmin={true}>
                <div className="border-t border-slate-200 my-4" />
              </PermissionGate>
            )}

            {/* Regular Navigation Links */}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  isActive(link.path, link.exact)
                    ? "text-slate-900 bg-slate-100 font-medium"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
              >
                <span className="mr-3">{link.icon}</span>
                {link.name}
              </Link>
            ))}
          </nav>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col w-full min-w-0">
        {/* Top Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10 border-b border-slate-200 pt-[env(safe-area-inset-top)]">
          <div className="px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
            {/* Mobile Logo */}
            <Link to="/" className="flex items-center lg:hidden">
              <img 
                src={logo} 
                alt="BuildCheap Logo" 
                className="w-8 h-8 object-contain mr-2"
              />
              <span className="text-lg font-bold text-slate-900">
                Build<span className="text-accent">Cheap</span>
              </span>
            </Link>

            {/* Desktop spacer */}
            <div className="hidden lg:block" />

            {/* Header Actions */}
            <div className="flex items-center gap-3">
              {/* User Menu (desktop only) */}
              {auth.isAuthenticated && (
                <div className="relative hidden lg:block" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center p-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-700/20 hover:bg-slate-50 transition-all duration-200"
                    aria-expanded={userMenuOpen}
                  >
                    <UserAvatar
                      src={user?.picture}
                      alt={userName}
                      className="w-8 h-8 rounded-lg object-cover mr-3"
                      fallback={
                        <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-white font-semibold text-sm mr-3">
                          {userName.charAt(0).toUpperCase()}
                        </div>
                      }
                    />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-900 leading-none">
                        {userName}
                      </p>
                      <p className="text-xs text-slate-600 mt-1 leading-tight max-w-32 truncate">
                        {userEmail}
                      </p>
                    </div>
                  </button>

                  {/* User Dropdown Menu */}
                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-slate-200 py-2 z-50 animate-in slide-in-from-top-2 duration-200">
                      <div className="px-4 py-3 border-b border-slate-200">
                        <p className="text-sm font-medium text-slate-900">
                          {userName}
                        </p>
                        <p className="text-xs text-slate-600 mt-1 truncate">
                          {userEmail}
                        </p>
                      </div>

                      <Link
                        to={profilePath}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 transition-colors duration-200"
                      >
                        <FaUser className="w-4 h-4 mr-3" />
                        Meu Perfil
                      </Link>

                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          setSupportOpen(true);
                        }}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:bg-slate-50 transition-colors duration-200"
                      >
                        <FaWhatsapp className="w-4 h-4 mr-3 text-emerald-600" />
                        Falar com o suporte
                      </button>

                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 focus:outline-none focus:bg-red-50 transition-colors duration-200"
                      >
                        <FaSignOutAlt className="w-4 h-4 mr-3" />
                        Sair da conta
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Login Button (when not authenticated) */}
              {!auth.isAuthenticated && !auth.isLoading && (
                <Link
                  to="/login"
                  className="bg-slate-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-slate-800 focus:ring-2 focus:ring-slate-700/20 focus:outline-none transition-all duration-200"
                >
                  Entrar
                </Link>
              )}
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 bg-slate-50 pb-[calc(4rem+env(safe-area-inset-bottom))] lg:pb-0">
          <div className="max-w-7xl mx-auto px-1 sm:px-3 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Navigation (mobile only - buyers) */}
      {auth.isAuthenticated && !isSeller && <BottomNav />}

      {/* Bottom Navigation (mobile only - sellers) */}
      {auth.isAuthenticated && isSeller && <SellerBottomNav />}

      {/* Modal de suporte (acionado pelo menu do usuário) */}
      <SupportModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
    </div>
  );
};

export default Header;
